import React, { forwardRef, useEffect, useCallback, useState, useRef } from 'react';

import { Box, Flex, Popover, PopoverBody, PopoverContent, PopoverTrigger, useDisclosure } from '@chakra-ui/react';
import { GenericInputControl } from 'components/RabbitOKRSidePanel/Components/ParentOKRComponent';
import SearchBar from 'components/RabbitOKRSidePanel/Components/SearchBar';
import CustomTagChipList from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/CustomTagChipList';
import CustomTagWithoutLabel from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/CustomTagWithoutLabel';
import { useToast } from 'components/hooks/useToast';
import { Divider1 } from 'styles/common';
import { themeColors } from 'themes/clients/baseTheme';

import { MeshLogo, Plus } from 'assets/assetsV1/iconComponents';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';
import { isNetchexLogin } from 'constants/netChexConstants';

import { Button } from 'uiToolkitV1/Button';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

const MeshLogoComponent = () => (
  <Tooltip label="Smart Data via Mesh">
    <Box>
      <MeshLogo ml="10px" />
    </Box>
  </Tooltip>
);

const ControlComponent = forwardRef((props, ref) => {
  const { isOpen, onToggle, tags, isReadOnly, onChange } = props;
  const mandatoryTagsLength = tags.filter((tag) => tag.isMandatory).length;
  let formattedTags = [];
  if (mandatoryTagsLength === 1) {
    formattedTags = tags.map((tag) => {
      const newTag = { ...tag };
      if (newTag.isMandatory) {
        newTag.isDisabled = true;
      }
      return newTag;
    });
  } else {
    formattedTags = tags;
  }

  return (
    <Box ref={ref}>
      {formattedTags.length > 0 ? (
        <GenericInputControl onClick={onToggle} w="100%" $isActive={isOpen} $isSolidWhenNotActive>
          <Box w="100%">
            <CustomTagChipList
              tags={formattedTags}
              tagsLimit={3}
              isShowCloseButton={false}
              onTagClose={(tag) => {
                onChange(tags.filter((cur) => cur.tagName !== tag?.tagName));
              }}
            />
          </Box>
        </GenericInputControl>
      ) : (
        <Button variant="tertiary" StartIcon={Plus} color="blue" onClick={onToggle} disabled={isReadOnly}>
          Add Tag
        </Button>
      )}
    </Box>
  );
});

const SelectionComponent = ({
  allTags = [],
  curSelectedTags = [],
  onClose = () => {},
  onSave = () => {},
  onChange = () => {},
  isOpen,
}) => {
  const [searchText, setSearchText] = useState('');
  const [formattedAllTags, setFormattedAllTags] = useState(allTags);
  const [displayTags, setDisplayTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const firstUpdate = useRef(true);
  const toast = useToast();

  const resetComponent = () => {
    setSearchText('');
    setSelectedTags(curSelectedTags);
  };

  useEffect(() => {
    setSelectedTags(curSelectedTags);
  }, [curSelectedTags]);

  useEffect(() => {
    setFormattedAllTags(allTags);
  }, [allTags]);

  useEffect(() => {
    if (isOpen === false) {
      resetComponent();
    }
  }, [isOpen]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      setDisplayTags([...formattedAllTags]);
      return;
    }

    if (searchText) {
      setDisplayTags(formattedAllTags.filter((tag) => tag?.tagName?.toLowerCase().includes(searchText.toLowerCase())));
    } else {
      setDisplayTags([...formattedAllTags]);
    }
  }, [allTags, searchText, formattedAllTags]);

  const handleInputChange = useCallback((e) => setSearchText(e.target.value), []);
  const handleClearInput = useCallback(() => {
    setSearchText('');
  }, []);

  const isTagChecked = (tag) => Boolean(selectedTags.find((selectedTag) => selectedTag.tagName === tag.tagName));

  useEffect(() => {
    onChange(selectedTags);
    const selectedMandatoryTags = selectedTags.filter((tag) => tag.isMandatory);
    const selectedMandatoryDisabledTags = selectedMandatoryTags.filter((tag) => tag.isDisabled);

    if (selectedMandatoryTags.length === 1 && selectedMandatoryDisabledTags.length === 0) {
      const newFormattedAllTags = formattedAllTags.map((tag) => {
        const newTag = { ...tag };
        if (newTag.tagName === selectedMandatoryTags[0]?.tagName) {
          newTag.isDisabled = true;
        }
        return newTag;
      });
      setFormattedAllTags(newFormattedAllTags);
      setSelectedTags(newFormattedAllTags.filter((tag) => isTagChecked(tag)));
    } else if (selectedMandatoryTags.length > 1 && selectedMandatoryDisabledTags.length > 0) {
      const newFormattedAllTags = formattedAllTags.map((tag) => {
        const newTag = { ...tag };

        if (newTag.isMandatory && tag.isDisabled && !tag?.isNotDefined) {
          newTag.isDisabled = false;
        }
        return newTag;
      });
      setFormattedAllTags(newFormattedAllTags);
      setSelectedTags(newFormattedAllTags.filter((tag) => isTagChecked(tag)));
    }
  }, [formattedAllTags, selectedTags]);

  const onCheckboxChange = (event, selectedTag) => {
    const isSelected = event.target.checked;
    if (isSelected) {
      setSelectedTags([...selectedTags, { ...selectedTag }]);
    } else {
      setSelectedTags(selectedTags.filter((tag) => tag.tagName !== selectedTag.tagName));
    }
  };

  const isModifiedCallback = useCallback(() => {
    if (selectedTags.length !== curSelectedTags.length) {
      return true;
    }

    let isModifiedFlag = false;
    const curSelectedTagMap = {};
    curSelectedTags.forEach((tag) => {
      curSelectedTagMap[tag.tagName] = true;
    });
    selectedTags.forEach((tag) => {
      if (!curSelectedTagMap[tag.tagName] || (curSelectedTagMap[tag.tagName] && !isTagChecked(tag))) {
        isModifiedFlag = true;
      }
    });
    return isModifiedFlag;
  }, [curSelectedTags, isTagChecked, selectedTags]);

  return (
    <>
      <Flex direction="column" px="10px">
        <Flex mt="15px">
          <SearchBar
            value={searchText}
            placeholder="Search data"
            onChange={handleInputChange}
            onClearInput={handleClearInput}
          />
        </Flex>
        <Flex gap="2px" px="8px">
          {selectedTags.length > 0 ? (
            <Flex align="center" sx={{ textWrap: 'nowrap' }}>
              <TextBodyMedium color="text.g1">Selected ({selectedTags.length}):</TextBodyMedium>
            </Flex>
          ) : null}
          <CustomTagChipList
            tags={selectedTags}
            tagsLimit={isMobileView() ? 1 : 3}
            isShowCloseButton
            containerProps={{ marginLeft: '10px', padding: '8px 0px' }}
            onTagClose={(selectedTag) => onCheckboxChange({ target: { checked: false } }, selectedTag)}
          />
        </Flex>
        <Divider1 />
        <Flex direction="column" height={isMobileView() ? '150px' : '239px'} overflow="auto">
          {displayTags.map((tag) => (
            <Tooltip
              isDisabled={!tag?.isDisabled}
              label={
                tag?.isMandatory && tag?.isSelected
                  ? `${tag.tagName} is  mandatory`
                  : tag?.disableReason
                    ? tag?.disableReason
                    : `${tag.tagName} is not defined`
              }
            >
              <Flex
                justify="space-between"
                padding="12px 16px 12px 16px"
                align="center"
                opacity={tag.isDisabled ? 0.4 : 1}
              >
                <Flex width="calc(100% - 54px)" overflow="auto" direction="column" gap="8px">
                  <Flex alignItems="center">
                    <CustomTagWithoutLabel key={tag.tagName} tag={tag} />
                    {!isNetchexLogin && <MeshLogoComponent />}
                  </Flex>

                  <TextBodyMedium
                    key={tag?.tagName}
                    color={themeColors.text.g2}
                    textWrap="wrap"
                    fontSize="12px"
                    lineHeight="16px"
                  >
                    {tag?.subtitle}
                  </TextBodyMedium>
                </Flex>
                <Checkbox
                  isChecked={isTagChecked(tag)}
                  // @ts-ignore
                  disabled={tag.isDisabled}
                  onChange={(event) => onCheckboxChange(event, tag)}
                  height="28px"
                />
              </Flex>
            </Tooltip>
          ))}
        </Flex>
      </Flex>
      <Flex
        flex="auto 0 0"
        borderTopWidth="1px"
        h="72px"
        justifyContent="flex-end"
        alignItems="center"
        backgroundColor={themeColors.neutral.F5F7F9}
      >
        <Button variant="tertiary" mr="auto" onClick={onClose}>
          Cancel
        </Button>

        <Button
          variant="filled"
          colorScheme="blue"
          onClick={() => {
            toast({
              title:
                'Data shared with Maven has been updated, which may lead to changes in results. Click on the Generate button to regenerate the result.',
              status: TOAST_STATUSES.INFO,
            });
            onSave(selectedTags);
          }}
          disabled={!isModifiedCallback()}
          mr="10px"
        >
          Apply
        </Button>
      </Flex>
    </>
  );
};

export const SelectAndSearchComponent = ({
  onChange = () => {},
  isReadOnly = false,
  tags = [],
  curSelectedTags = [],
  containerProps = {},
}) => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [popupWidth, setPopupWidth] = useState(0);

  const controlComponentRef = useCallback((node) => {
    if (node !== null) {
      setPopupWidth(node.getBoundingClientRect().width);
    }
  }, []);

  const handleTagClick = (event) => {
    if (isReadOnly) {
      return;
    }
    if (event) {
      event.persist();
      // model is opened on click of tag, but if user clicks on cross svg icon on tag, we are overriding the event.
      if (event.target.tagName === 'svg' || event.target.tagName === 'path') {
        return;
      }
    }

    onToggle();
  };

  const getPlacementPosition = () => {
    // size based on height of popup *2 + height tag input
    const MIN_SIZE_TO_ACCOMMODATE_TOP_POSITION = 972;
    return window.innerHeight > MIN_SIZE_TO_ACCOMMODATE_TOP_POSITION ? 'bottom' : 'auto';
  };

  return (
    <Box {...containerProps}>
      <Popover
        flip
        returnFocusOnClose={false}
        isOpen={isOpen}
        onClose={() => {
          onClose();
        }}
        padding="0px"
        placement={getPlacementPosition()}
      >
        <PopoverTrigger>
          <ControlComponent
            onToggle={handleTagClick}
            isOpen={isOpen}
            tags={curSelectedTags}
            ref={controlComponentRef}
            isReadOnly={isReadOnly}
            onChange={onChange}
            handleTagsSave={onClose}
          />
        </PopoverTrigger>
        <PopoverContent maxW={`${popupWidth}px`} width={`${popupWidth}px`} padding="0px">
          <PopoverBody padding="0px">
            <SelectionComponent
              allTags={tags}
              curSelectedTags={curSelectedTags}
              onClose={onClose}
              onSave={(selectedTags) => {
                onChange(selectedTags);
                onClose();
              }}
              // onChange={setControlComponentSelectedTags}
              isOpen={isOpen}
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
